import React, { useRef } from 'react'
import { observer } from 'mobx-react-lite'
import Link from 'next/link'
import { Button } from 'components/ui'
import PlayIcon from 'icons/Play'
import { useElementSize, useStore } from 'hooks'
import { PlaylistsStore } from 'stores/playlists'
import { playlistVideoSidebarEvents, brandVideoSidebarEvents, popularVideoSidebarEvents } from 'lib/ga'
import { truncateString, storyLink, playlistLink, applyFastlyParams, THEME } from 'utils'
import StoryThumbnail from 'components/StoryThumbnail'
import ZeusAd from 'components/ZeusAd'

const VideoList = observer(
  ({ title, type, items, currentItem, articleHeight, loadMore, isLoading, showButton, adSettings }) => {
    const playlistsStore = useStore(PlaylistsStore)
    const headerRef = useRef(null)
    const headerRefSize = useElementSize(headerRef)

    const handleItemClick = (url) => {
      if (type === 'playlists') {
        playlistVideoSidebarEvents.itemClicked(url)
      } else if (currentItem.brand_id) {
        brandVideoSidebarEvents.itemClicked(url)
      } else {
        popularVideoSidebarEvents.itemClicked(url)
      }
    }

    const handleLoadMore = () => {
      if (type === 'playlists') {
        playlistVideoSidebarEvents.loadMore()
      } else if (currentItem.brand_id) {
        brandVideoSidebarEvents.loadMore()
      } else {
        popularVideoSidebarEvents.loadMore()
      }
      loadMore()
    }
    return (
      <>
        <div className='videoList'>
          <div ref={headerRef}>
            <div className='videoList__ad'>
              <ZeusAd
                id='Sidebar-Top'
                media={{ minWidth: THEME.BREAKPOINTS.SM }}
                placeholderWidth={300}
                placeholderHeight={250}
                adSettings={adSettings}
              />
            </div>
            <div className='videoList__header'>
              <h3 className='videoList__title'>{title}</h3>
              <p className='videoList__count'>{items.length} videos</p>
            </div>
          </div>
          <div className='videoList__items'>
            {items.map((video, idx) => {
              const isCurrentVideo = currentItem.id === video.id
              let linkProps = storyLink(video)

              if (type === 'playlists') {
                const { href, as } = playlistLink(playlistsStore.current)
                linkProps = {
                  href: {
                    pathname: href,
                    query: { video: video.id }
                  },
                  as: `${as}?video=${video.id}`
                }
              }
              return (
                <Link key={video.id} {...linkProps}>
                  <a className='videoListItem' onClick={() => handleItemClick(video.url)}>
                    <span className='videoListItem__index'>
                      {isCurrentVideo ? <PlayIcon width='auto' height='10px' color='#999' /> : idx + 1}
                    </span>
                    <div className='videoListItem__image'>
                      <StoryThumbnail
                        src={applyFastlyParams(
                          video.thumbnail.raw_desktop,
                          !video.thumbnail.raw_desktop.includes('.gif')
                            ? { crop: 'smart', width: 120, height: 68 }
                            : { width: 120, height: 68 }
                        )}
                        alt={video.title}
                        aspectRatio='16:9'
                      />
                      {isCurrentVideo ? (
                        <span>Now Playing</span>
                      ) : (
                        // Disable displaying duration until we backfill more videos
                        // <span>{convertToDuration(video?.post_type_meta?.barstool_original?.duration)}</span>
                        <span></span>
                      )}
                    </div>
                    <p className='videoListItem__title'>{truncateString(video.title, 90)}</p>
                  </a>
                </Link>
              )
            })}
            {showButton && (
              <div className='videoList__button'>
                <Button
                  onClick={handleLoadMore}
                  isLoading={isLoading}
                  disabled={isLoading}
                  spinnerStyle={{ color: 'white' }}
                >
                  Load More
                </Button>
              </div>
            )}
          </div>
        </div>
        <style jsx>{`
          .videoList {
            position: relative;
            background: ${THEME.COLORS.LIGHTESTGRAY};
            box-shadow: ${THEME.BOX_SHADOW.DEFAULT};
          }
          .videoList__ad {
            text-align: center;
            padding-top: 12px;
            padding-bottom: 12px;
          }
          .videoList__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 16px;
          }
          .videoList__title {
            font-family: ${THEME.FONTS.BEBAS};
            font-size: 21px;
            font-weight: 400;
            text-transform: uppercase;
            margin: 0;
          }
          .videoList__count {
            color: #999;
            font-size: 11px;
            font-weight: bold;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            margin: 0;
          }
          .videoList__items {
            height: auto;
          }
          .videoList__button {
            padding: 8px 16px;
          }
          .videoListItem {
            position: relative;
            display: flex;
            padding: 8px 16px;
            border-top: 1px solid ${THEME.COLORS.LIGHTGRAY};
          }
          .videoListItem__index {
            position: absolute;
            top: 8px;
            right: 16px;
            color: #999;
            font-size: 11px;
            font-weight: bold;
            letter-spacing: 0.55px;
          }
          .videoListItem__image {
            width: 120px;
            position: relative;
            overflow: hidden;
            margin: 0 12px 0 0;
          }
          .videoListItem__image img {
            display: block;
            width: 120px;
            max-width: none;
            height: 68;
            object-fit: contain;
            background-color: ${THEME.COLORS.LIGHTGRAY};
          }
          .videoListItem__image span {
            display: block;
            position: absolute;
            bottom: 0;
            left: 0;
            background: rgba(0, 0, 0, 0.8);
            color: #fff;
            font-size: 12px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.1;
            padding: 2px 4px;
          }
          .videoListItem__image span:empty {
            display: none;
          }
          .videoListItem__title {
            flex: 1;
            color: ${THEME.COLORS.TEXTCOLOR};
            font-size: 14px;
            font-weight: bold;
            line-height: 18px;
            padding-right: 20px;
          }
          @media (min-width: ${THEME.BREAKPOINTS.SM}px) {
            .videoList {
              margin: 0;
            }
            .videoList__items {
              height: ${articleHeight - headerRefSize.height + 'px'};
              overflow-y: auto;
            }
          }
        `}</style>
      </>
    )
  }
)

export default VideoList
